import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, Gauge, CheckCircle, XCircle, Star, SlidersHorizontal } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { SectionHead } from "@/components/site/Promotions";
import { api } from "@/lib/api";
import { BUSINESS } from "@/config";

const ANY = "all";

export const SearchInventory = () => {
  const [filters, setFilters] = useState({ brands: [], categories: [], widths: [], aspects: [], diameters: [] });
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState(ANY);
  const [category, setCategory] = useState(ANY);
  const [width, setWidth] = useState(ANY);
  const [aspect, setAspect] = useState(ANY);
  const [diameter, setDiameter] = useState(ANY);
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (brand !== ANY) params.brand = brand;
    if (category !== ANY) params.category = category;
    if (width !== ANY) params.width = width;
    if (aspect !== ANY) params.aspect = aspect;
    if (diameter !== ANY) params.diameter = diameter;
    try {
      const r = await api.get("/tires", { params });
      setTires(r.data);
    } catch (e) {
      setTires([]);
    }
    setLoading(false);
  }, [q, brand, category, width, aspect, diameter]);

  useEffect(() => { search(); }, [search]);

  const reset = () => {
    setQ(""); setBrand(ANY); setCategory(ANY); setWidth(ANY); setAspect(ANY); setDiameter(ANY);
  };

  const waLink = (t) =>
    `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(
      `Hi! I'm interested in the ${t.brand} ${t.model} (${t.size}). Is it available?`
    )}`;

  return (
    <section id="search" className="relative py-20 sm:py-28 bg-background z-10 grain" data-testid="search-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead index="02" kicker="Live Inventory Search" title="Find Your Exact Fit" />

        {/* Filter panel */}
        <div className="mt-10 border border-border bg-card/60 backdrop-blur p-5 sm:p-6" data-testid="search-panel">
          <div className="flex items-center gap-2 mb-5 text-primary">
            <SlidersHorizontal size={18} weight="bold" />
            <span className="text-xs font-bold tracking-[0.25em] uppercase">Filter By Size, Brand & Type</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <FilterSelect label="Width" value={width} onChange={setWidth} options={filters.widths} testid="filter-width" />
            <FilterSelect label="Aspect" value={aspect} onChange={setAspect} options={filters.aspects} testid="filter-aspect" />
            <FilterSelect label="Rim (in)" value={diameter} onChange={setDiameter} options={filters.diameters} testid="filter-diameter" />
            <FilterSelect label="Brand" value={brand} onChange={setBrand} options={filters.brands} testid="filter-brand" />
            <FilterSelect label="Type" value={category} onChange={setCategory} options={filters.categories} testid="filter-category" />
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">Keyword</label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="e.g. P Zero"
                  className="pl-9 bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1"
                  data-testid="filter-keyword"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground" data-testid="results-count">
              <span className="text-primary font-bold">{tires.length}</span> match{tires.length === 1 ? "" : "es"} found
            </p>
            <button
              onClick={reset}
              className="text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors"
              data-testid="filter-reset"
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="results-grid">
          <AnimatePresence mode="popLayout">
            {tires.map((t, i) => (
              <motion.article
                key={t.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                className="group border border-border bg-card hover:border-primary/60 transition-colors flex flex-col"
                data-testid="tire-card"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-black">
                  <img src={t.image} alt={`${t.brand} ${t.model}`} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" loading="lazy" />
                  <span className="absolute top-3 left-3 bg-black/70 backdrop-blur text-primary text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-sm">
                    {t.category}
                  </span>
                  <span className={`absolute top-3 right-3 flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-1 rounded-sm ${t.stock > 0 ? "bg-primary/90 text-white" : "bg-red-500/90 text-white"}`}>
                    {t.stock > 0 ? <CheckCircle size={12} weight="fill" /> : <XCircle size={12} weight="fill" />}
                    {t.stock > 0 ? `${t.stock} in stock` : "Out"}
                  </span>
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">{t.brand}</span>
                    <span className="flex items-center gap-1 text-xs text-primary"><Star size={12} weight="fill" /> {t.rating}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-1">{t.model}</h3>
                  <div className="flex items-center gap-1.5 mt-2 text-sm text-foreground/70">
                    <Gauge size={16} weight="bold" className="text-primary" /> {t.size}
                  </div>

                  <div className="mt-auto pt-4 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Per tire</div>
                      <div className="font-display font-black text-2xl text-foreground">${t.price.toFixed(2)}</div>
                    </div>
                    <a
                      href={waLink(t)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold uppercase tracking-wide bg-primary text-white px-3 py-2.5 rounded-sm hover:bg-red-500 transition-colors"
                      data-testid={`tire-inquire-${t.id}`}
                    >
                      Inquire
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        {!loading && tires.length === 0 && (
          <div className="text-center py-20 border border-dashed border-border mt-8" data-testid="no-results">
            <p className="font-display font-black text-3xl uppercase tracking-tight text-muted-foreground">No matches</p>
            <p className="text-muted-foreground mt-2">Try widening your filters or message us on WhatsApp — we source daily.</p>
          </div>
        )}
      </div>
    </section>
  );
};

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-background border-border rounded-sm h-11 focus:ring-primary focus:ring-1 data-[placeholder]:text-muted-foreground" data-testid={testid}>
        <SelectValue placeholder="Any" />
      </SelectTrigger>
      <SelectContent className="bg-popover border-border max-h-64">
        <SelectItem value="all">Any</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={String(o)}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
